import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import JobTimer from './JobTimer';
import QuickCloseJobModal from './QuickCloseJobModal';
import { WarrantyStatus } from './WarrantyStatus';
import { ApexReference, JobSearch } from './ApexReference';
import { JobDetailLauncher } from './JobDetailModal';
import { matchesJobSearch } from '../lib/jobReference';

export default function TodaysJobsView({ onEditJob, refreshKey }) {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [todaysJobs, setTodaysJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closingAppointment, setClosingAppointment] = useState(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('All'); // Dropdown selection
  const [appliedFilter, setAppliedFilter] = useState('All'); // Applied filter
  const [businessTimezone, setBusinessTimezone] = useState(null);
  const [timezoneFetched, setTimezoneFetched] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const init = async () => {
      await fetchBusinessSettings();
      setTimezoneFetched(true);
    };
    init();
  }, []);

  useEffect(() => {
    if (timezoneFetched && businessTimezone) {
      fetchTodaysJobs();
    }
  }, [timezoneFetched, businessTimezone, refreshKey]);

  const fetchBusinessSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/business`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.timezone) {
        setBusinessTimezone(response.data.timezone);
      } else {
        // Fallback to Adelaide if no timezone in settings
        setBusinessTimezone('Australia/Adelaide');
      }
    } catch (error) {
      console.error('Error fetching business settings:', error);
      // Default to Adelaide if we can't get settings
      setBusinessTimezone('Australia/Adelaide');
    }
  };

  const fetchTodaysJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setTodaysJobs([]);
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/${user.is_admin ? 'admin/appointments' : 'appointments'}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Get today's date in business timezone
      // Use Intl API to get the date in the business timezone
      let today;
      try {
        const formatter = new Intl.DateTimeFormat('en-CA', { 
          timeZone: businessTimezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
        today = formatter.format(new Date());
      } catch (error) {
        // Fallback to local timezone if business timezone is invalid
        console.warn('Invalid timezone, using local:', error);
        today = new Date().toISOString().split('T')[0];
      }
      
      // Filter for today's jobs only
      const filtered = response.data.filter(apt => apt.appointment_date === today);
      
      // Sort by start time
      filtered.sort((a, b) => a.start_time.localeCompare(b.start_time));
      
      setTodaysJobs(filtered);
    } catch (error) {
      console.error('Error fetching today\'s jobs:', error);
      setTodaysJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
    toast.success('Address copied!');
  };

  const handleClose = (appointment) => {
    setClosingAppointment(appointment);
    setShowCloseModal(true);
  };

  const openInWaze = (address) => {
    const encoded = encodeURIComponent(address);
    window.open(`https://waze.com/ul?q=${encoded}&navigate=yes`, '_blank');
  };

  const openInGoogleMaps = (address) => {
    const encoded = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
  };

  const getStatusColor = (status) => {
    const colors = {
      'Open': '#00FF00',
      'In Progress': '#FFD60A',
      'Waiting for Parts': '#FF6B00',
      'Waiting for Customer Update': '#00D4FF',
      'Waiting for Customer': '#00D4FF',
    };
    return colors[status] || '#FFFFFF';
  };

  if (loading) {
    return (
      <div className="retro-panel">
        <div className="text-center" style={{ color: '#FF6B00' }}>
          [LOADING TODAY'S JOBS...]
        </div>
      </div>
    );
  }

  // Filter today's jobs based on applied filter (case-insensitive)
  const filteredJobs = (appliedFilter === 'All' || appliedFilter === 'All Jobs'
    ? todaysJobs 
    : todaysJobs.filter(job => {
        const jobStatus = (job.status || '').trim();
        return jobStatus.toLowerCase() === appliedFilter.toLowerCase();
      })).filter(job => matchesJobSearch(job, search));
  
  const handleApplyFilter = () => {
    setAppliedFilter(selectedFilter);
  };

  return (
    <div className="retro-panel">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4 md:mb-6">
        <h2 className="text-2xl md:text-3xl font-bold glow-text" style={{ color: '#00FF00' }}>
          [TODAY'S JOBS] - {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: businessTimezone })}
        </h2>
        
        {/* Status Filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-[var(--accent)] font-bold text-sm">FILTER:</label>
          <select
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
            className="px-3 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)] font-bold text-sm"
          >
            <option value="All">All</option>
            <option value="Open">Open</option>
            <option value="Scheduled">Scheduled</option>
            <option value="In Progress">In Progress</option>
            <option value="Closed">Closed</option>
          </select>
          <button
            onClick={handleApplyFilter}
            className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white font-bold border-2 border-green-600 text-sm"
          >
            [APPLY]
          </button>
        </div>
      </div>

      <JobSearch scope="today" value={search} onChange={setSearch} />
      {filteredJobs.length === 0 ? (
        <div className="text-center text-lg p-8 border-2" style={{ color: '#00FF00', borderColor: '#00FF00' }}>
          ✓ NO {appliedFilter.toUpperCase()} JOBS FOR TODAY
        </div>
      ) : (
        <div className="space-y-3 md:space-y-4">
          {filteredJobs.map((job) => (
            <div
              key={job.id}
              className="border-2 p-3 md:p-4"
              style={{ 
                borderColor: getStatusColor(job.status),
                background: `${getStatusColor(job.status)}10`
              }}
            >
              {/* Time and Timer */}
              <div className="mb-3"><ApexReference job={job} scope="today" /></div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
                <div className="text-xl md:text-2xl font-bold" style={{ color: getStatusColor(job.status) }}>
                  {job.start_time} - {job.end_time}
                </div>
                {job.assigned_to_user_id === user.id && <JobTimer appointment={job} onUpdate={fetchTodaysJobs} />}
              </div>

              {/* Customer Name */}
              <div className="text-xl md:text-2xl font-bold mb-2" style={{ color: '#ffffff' }}>
                {job.customer_name}
              </div>

              {/* Address with Copy Button */}
              <div className="mb-3 p-3 border-2" style={{ borderColor: '#FF6B00', backgroundColor: '#0a0a0a' }}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="text-sm font-bold mb-1" style={{ color: '#FF6B00' }}>
                      [ADDRESS]:
                    </div>
                    <div className="text-base md:text-lg" style={{ color: '#ffffff' }}>
                      {job.location}
                    </div>
                  </div>
                  <button
                    onClick={() => copyAddress(job.location)}
                    className="retro-btn retro-btn-green shrink-0"
                    style={{ minWidth: 'auto', padding: '8px 12px' }}
                  >
                    [COPY]
                  </button>
                </div>

                {/* Map Navigation Buttons */}
                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                  <button
                    onClick={() => openInWaze(job.location)}
                    className="retro-btn retro-btn-yellow flex-1"
                  >
                    [OPEN IN WAZE]
                  </button>
                  <button
                    onClick={() => openInGoogleMaps(job.location)}
                    className="retro-btn retro-btn-yellow flex-1"
                  >
                    [OPEN IN GOOGLE MAPS]
                  </button>
                </div>
              </div>

              {/* Job Info Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-2">
                <div>
                  <span style={{ color: '#FF6B00' }}>Job Type:</span>{' '}
                  <span style={{ color: '#ffffff' }}>{job.job_type}</span>
                </div>
                <div>
                  <span style={{ color: '#FF6B00' }}>Priority:</span>{' '}
                  <span style={{ color: getStatusColor(job.status), fontWeight: 700 }}>
                    {job.priority}
                  </span>
                </div>
              </div>

              {/* Description */}
              <WarrantyStatus appointment={job} />
              <div className="mt-2">
                <div className="text-sm font-bold" style={{ color: '#FF6B00' }}>Description:</div>
                <div style={{ color: '#ffffff' }}>{job.description}</div>
              </div>

              {/* Notes if any */}
              {job.notes && (
                <div className="mt-2 p-2 border" style={{ borderColor: '#00D4FF', backgroundColor: '#001a1a' }}>
                  <div className="text-sm font-bold" style={{ color: '#00D4FF' }}>Notes:</div>
                  <div className="text-sm" style={{ color: '#ffffff' }}>{job.notes}</div>
                </div>
              )}

              {/* Action Button */}
              {user.is_admin && <button type="button" data-testid={`today-admin-edit-${job.id}`} className="retro-btn mt-3" onClick={() => onEditJob(job)}>[EDIT JOB]</button>}
              <div className="mt-3"><JobDetailLauncher appointment={job} onUpdate={fetchTodaysJobs} scope="today" /></div>
              {job.status !== 'Closed' && job.assigned_to_user_id === user.id && (
                <div className="mt-3">
                  <button
                    onClick={() => handleClose(job)}
                    data-testid={`today-close-job-${job.id}`}
                    className="retro-btn retro-btn-green w-full"
                  >
                    [CLOSE JOB]
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showCloseModal && closingAppointment && (
        <QuickCloseJobModal
          appointment={closingAppointment}
          onSuccess={() => {
            setShowCloseModal(false);
            setClosingAppointment(null);
            fetchTodaysJobs();
          }}
          onClose={() => {
            setShowCloseModal(false);
            setClosingAppointment(null);
          }}
        />
      )}
    </div>
  );
}
